/**
 * @fileoverview React Router integration for AppProvider, providing routing
 * context and Link component for navigation.
 */

import * as React from 'react';
import { useSearchParams, useLocation, useNavigate, Link as ReactRouterLink } from 'react-router';
import { AppProvider, type AppProviderProps, Navigate, Router } from './AppProvider';
import { LinkProps } from '../components/Link';

const Link = React.forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => {
  const { href, history, ...rest } = props;
  return <ReactRouterLink ref={ref} to={href} replace={history === 'replace'} {...rest} />;
});

function ReactRouterAppProvider(props: AppProviderProps): React.JSX.Element {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const navigateImpl = React.useCallback<Navigate>(
    (url, { history = 'auto' } = {}) => {
      if (history === 'auto' || history === 'push') {
        return navigate(url);
      }
      if (history === 'replace') {
        return navigate(url, { replace: true });
      }
      throw new Error(`Invalid history option: ${history}`);
    },
    [navigate],
  );

  const routerImpl = React.useMemo<Router>(
    () => ({
      pathname,
      searchParams,
      navigate: navigateImpl,
      Link,
    }),
    [pathname, searchParams, navigateImpl],
  );

  return <AppProvider router={routerImpl} {...props} />;
}

export { ReactRouterAppProvider };

